// App.tsx
import { useEffect, useState, type FC } from "react";
import { Helmet } from "react-helmet";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { FluentProvider, Button } from '@fluentui/react-components';
import { SignOutRegular } from "@fluentui/react-icons";
import { Userpilot } from 'userpilot'

import { useAuth } from './context/AuthContext';
import { useSignInState } from './Components/signInState';
import { useHandleLogout } from './Components/HandleLogout';
import LoadingPage from './Components/LoadingPage';
import { lightPowerONTheme, darkPowerONTheme } from './Helpers/fluentPowerONTheme';
import { formatDateDDMMYYYY, getUtcOffsetInHours } from './Helpers/dateUtils';
import { WorkspaceFeatureIds } from './constants/workspaceFeatureIds';
import {
  hasActiveWorkspaceFeature,
  isWorkspaceSkuUsable,
} from './services/workspaceSubscription';
import AppLayout from './Layout/AppLayout';
import NotFoundPage from './NotFoundPage';

import { WelcomePage } from './WelcomePage';
import IntuneDashboards from './IntuneDashboards';
import CeateDashboard from './CreateDashboards';
import CustomDashboards from './CustomDashboards';
import { AdminPageRoute } from './AccountAdmin';
import ProfilePage from './ProfilePage';
import PromoPage from './PromoPage';
import SignUpFlow from './SignUpFlow';
import UserpilotRouteTracker from './Components/UserpilotRouteTracker';
import { useConfig } from "./context/ConfigContext";

Userpilot.initialize(process.env.REACT_APP_USERPILOT_API_KEY || '');

declare let $: any;

let bffLoginRedirectIssuedForUnauthenticatedShell = false;

const UnauthenticatedBffLoginRedirect: FC = () => {
  const { login } = useAuth();
  useEffect(() => {
    if (bffLoginRedirectIssuedForUnauthenticatedShell)
      return;
    bffLoginRedirectIssuedForUnauthenticatedShell = true;
    login();
  }, [login]);
  return (
    <div
      className="siginwait"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100vh' }}
    />
  );
};

const App = () => {
  const { isAuthChecked, isAuthenticated } = useAuth();
  const { appName } = useConfig();
  const { signInState } = useSignInState();
  const { handleLogout } = useHandleLogout();
  const [shouldRegister, setShouldRegister] = useState<boolean>(false);
  const [currentTheme, setCurrentTheme] = useState(darkPowerONTheme);
  const userInfo = signInState?.userInfo;

  const workspaceSub = signInState.workspaceSubscription;
  const bypassWorkspaceGuards = signInState.isWorkspaceSuperAdmin;

  // Full-screen blocker trigger: a signed-in non-super-admin whose workspace SKU is
  // missing, soft-disabled in Basecamp (IsActive=false), or past its ExpirationDate
  // must not see <AppLayout />. We render <SignUpFlow /> instead, which contains
  // dedicated copy per state (no SKU / disabled / trial expired / subscription expired).
  const isWorkspaceBlocked
    = signInState.isAuthed
      && !bypassWorkspaceGuards
      && !isWorkspaceSkuUsable(workspaceSub);

  const canAccessIntuneDashboards = () =>
    hasActiveWorkspaceFeature(workspaceSub, WorkspaceFeatureIds.ClarityOutOfBoxReports, bypassWorkspaceGuards);

  const canAccessCustomDashboards = () =>
    hasActiveWorkspaceFeature(workspaceSub, WorkspaceFeatureIds.ClarityCustomReports, bypassWorkspaceGuards);

  useEffect(() => {
    setCurrentTheme(userInfo?.ThemePreference === 'light' ? lightPowerONTheme : darkPowerONTheme);

    if (userInfo) {
      const userpilotData: any = {
        name: userInfo.DisplayName || `${userInfo.FirstName ?? ''} ${userInfo.LastName ?? ''}`.trim(),
        email: userInfo.Email,
        role: 'Admin',
        timezone: getUtcOffsetInHours(),
      };

      const customerInfo = signInState?.customerInfo?.[0];
      const sku = workspaceSub?.skuDetails;

      if (customerInfo) {
        userpilotData.company = {
          id: customerInfo.CustomerID,
          name: customerInfo.CompanyName,
          azureTenantId: customerInfo.TenantID,
          createdAt: formatDateDDMMYYYY(customerInfo.CreationDate),
          ...(sku &&
            isWorkspaceSkuUsable(workspaceSub, bypassWorkspaceGuards) && {
              onFreeTrial: sku.isTrial,
              planEnds: formatDateDDMMYYYY(sku.expirationDate),
              sku: `SKU ${sku.skuId}`,
            }),
        };
      }

      Userpilot.identify(`${userInfo.IdentityUserID}`, userpilotData);
    }
  }, [signInState]);

  function withWorkspaceFeatureGate(
    check: () => boolean,
    WrappedComponent: React.ComponentType<any>,
  ) {
    return (props: any) => {
      if (!check()) {
        return <Navigate to="/" />;
      }

      return <WrappedComponent {...props} />
    };
  };

  function withCreateDashboardGate(WrappedComponent: React.ComponentType<any>) {
    return (props: any) => {
      if (!hasActiveWorkspaceFeature(workspaceSub, WorkspaceFeatureIds.ClarityCustomReports, bypassWorkspaceGuards)) {
        return <Navigate to="/" />;
      }
      return <WrappedComponent {...props} />;
    };
  }

  useEffect(() => {
    const hasUser = !!signInState.userInfo;
    const customerCount = signInState.customerInfo?.length ?? 0;
    if (!shouldRegister && !signInState.isLoading && !signInState.isAuthed && (!hasUser || customerCount === 0)) {
      setShouldRegister(true);
    } else if (hasUser && customerCount > 0) {
      setShouldRegister(false);
    }
  }, [signInState]);

  return (
    <FluentProvider theme={currentTheme}>
      <Helmet>
        <meta name="description" content={`${appName} App`} />
        <title>{appName}</title>
      </Helmet>
      <Router>
        <UserpilotRouteTracker />
        <Routes>
          <Route path="/*" element={
            <>
              {!isAuthChecked && (
                <div className="siginwait" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100vh' }}>
                  <LoadingPage message="Checking session..." />
                </div>
              )}
              {isAuthChecked && !isAuthenticated && <UnauthenticatedBffLoginRedirect />}
              {isAuthChecked && isAuthenticated && (
                <>
                {!shouldRegister && !signInState.isAuthed && signInState.isLoading &&
                  <div className="siginwait" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100vh' }}>
                    <LoadingPage message="Checking signin details..." />
                    <div className="logoutbutton" style={{ margin: '20px' }}>
                      <Button size="medium" icon={<SignOutRegular />} onClick={handleLogout} >
                        Logout
                      </Button>
                      
                    </div>
                  </div>
                }
                {signInState.isAuthed && signInState.userInfo && signInState.customerInfo && !isWorkspaceBlocked &&
                  <AppLayout>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          canAccessIntuneDashboards() ? (
                            <Navigate to="/intunedashboards" replace />
                          ) : (
                            <Navigate to="/customdashboards" replace />
                          )
                        }
                      />
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/intunedashboards/:tab?" element={withWorkspaceFeatureGate(canAccessIntuneDashboards, IntuneDashboards)({ currentCustomerID: signInState.currentCustomerID })} />
                      <Route path="/createdash" element={withCreateDashboardGate(CeateDashboard)({ currentCustomerID: signInState.currentCustomerID })} />
                      <Route path="/customdashboards" element={withWorkspaceFeatureGate(canAccessCustomDashboards, CustomDashboards)({ currentCustomerID: signInState.currentCustomerID })} />
                      <Route path="/admin" element={<AdminPageRoute />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/promo" element={<PromoPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </AppLayout>
                }
                {(shouldRegister || isWorkspaceBlocked) && <SignUpFlow />}
                </>
              )}
            </>
          } />
        </Routes>
      </Router>
    </FluentProvider>
  );
};

export default App;
